import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import { t } from '@/lib/strings';

interface NotesPanelProps {
  rationale: string;
  internalNote: string;
  onRationaleChange: (value: string) => void;
  onInternalNoteChange: (value: string) => void;
  /**
   * The internal note exists for a sales rep to leave themselves a reminder — it is never shown
   * in the report either way (AssessmentService.Rationales strips it). In the self-service flow
   * there is no rep to write one, so the field itself is hidden rather than offered to a customer
   * with no reason to use it. Defaults to true so the sales flow is unchanged.
   */
  showInternalNote?: boolean;
}

/**
 * The rationale is shown in the customer's report; the internal note never is. The panel stays
 * collapsed behind a button so the customer does not read the sales rep's notes off the screen
 * (specification 8.4).
 */
export function NotesPanel({
  rationale,
  internalNote,
  onRationaleChange,
  onInternalNoteChange,
  showInternalNote = true,
}: NotesPanelProps) {
  const [open, setOpen] = useState(false);
  const filled = rationale.length > 0 || (showInternalNote && internalNote.length > 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls="notes-panel"
      >
        {open ? t('handling.skjulNote') : t('handling.visNote')}
        {!open && filled ? ' •' : ''}
      </Button>

      <Collapse in={open} unmountOnExit>
        <Box id="notes-panel" sx={{ display: 'grid', gap: 2.5, mt: 2 }}>
          <TextField
            label={t('skema.begrundelse')}
            helperText={t('skema.begrundelseHjaelp')}
            value={rationale}
            onChange={(event) => onRationaleChange(event.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />

          {showInternalNote && (
            <TextField
              label={t('skema.saelgernote')}
              helperText={t('skema.saelgernoteHjaelp')}
              value={internalNote}
              onChange={(event) => onInternalNoteChange(event.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
